import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { getLandingPageData } from "@/api/server/landing-page";
import { getSingleProduct } from "@/api/server/product";
import ProductDetailsTab from "@/components/sections/productDetailsTabs";
import ProductPreview from "@/components/sections/productPreview";
import SimilarProducts from "@/components/sections/similarProduct";
import MetaData from "@/components/seo/MetaData";
import Breadcrumbs from "@/components/ui/navigation/breadcrumbs";
import { getWishlistState } from "@/store/feature/wishlistSlice";
import statusCodes from "@/utils/constants/http-status-codes";
import { revalidationTime } from "@/utils/constants/server-side.constants";
import { updateListWithWishlistStatus } from "@/utils/helpers/wishlist";

function ProductDetails({ product, schema, metadata, similarProducts }) {
  const [moreProducts, setMoreProducts] = useState(similarProducts);

  const { wishlistItems } = useSelector(getWishlistState);

  const categoryBreadcrumbs =
    product?.categories?.map((el) => ({
      value: el?.name,
      link: `category/${el?.slug}`,
    })) || [];

  useEffect(() => {
    setMoreProducts(similarProducts);
  }, [similarProducts]);

  useEffect(() => {
    if (!Object.keys(wishlistItems).length) return;

    setMoreProducts(updateListWithWishlistStatus(moreProducts, wishlistItems));
  }, [Object.keys(wishlistItems).length]);

  return (
    <>
      <MetaData {...metadata} schema={schema} type="product" />

      <div className="container pt-[2rem]">
        <div className="mb-[0.85rem] md:mb-[1.75rem]">
          <Breadcrumbs
            breadcrumbs={[
              { link: "", value: "Home" },
              ...categoryBreadcrumbs,
              { link: "", value: product?.name },
            ]}
          />
        </div>

        <ProductPreview productData={product} />
      </div>
      <div className="container pb-[5.625rem] pt-[4rem] lg:pt-[4.813rem]">
        <h2 className="text-[1.125rem] font-semibold lg:text-[1.375rem]">
          About this Item
        </h2>
        <div className="pt-[2.25rem] lg:pt-[2.563rem]">
          <ProductDetailsTab
            productName={product?.name}
            data={product?.descriptive_information}
          />
        </div>
        <div className="pt-[3.5rem] lg:pt-[2.5rem]">
          <SimilarProducts products={moreProducts} />
        </div>
      </div>
    </>
  );
}

export async function getStaticPaths() {
  const res = await getLandingPageData();
  const resData = res.data?.data;

  const {
    popular_products: popularProducts,
    new_products: newProducts,
    deal_products: dealProducts,
    special_products: specialProducts,
    category_products,
  } = resData;

  const categoryProducts = category_products
    ?.map(({ products }) => products)
    ?.flat();

  const hotProducts = [
    ...popularProducts,
    ...dealProducts,
    ...newProducts,
    ...specialProducts,
    ...categoryProducts,
  ];
  const paths = hotProducts.map(({ slug }) => ({
    params: { product_slug: slug },
  }));

  return { paths, fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  const props = {
    schema: null,
    product: null,
    metadata: null,
    similarProducts: null,
  };

  const res = await getSingleProduct(params.product_slug);

  if (res?.status === statusCodes.OK) {
    props.schema = res?.data?.data?.schema;
    props.product = res?.data?.data?.product;
    props.metadata = res?.data?.data?.metadata;
    props.similarProducts = res?.data?.data?.similar_products;
  }

  if (!props.product) {
    return {
      notFound: true,
    };
  }

  return { props, revalidate: revalidationTime };
}

export default ProductDetails;
